export const isProfileItemDisabled = (item) => {
  if (!item) return false;
  const status = item.status;
  return status === 0 || status === "0" || status === false;
};

export const isProfileItemEnabled = (item) => {
  if (!item) return false;
  const status = item.status;
  return status === 1 || status === "1" || status === true;
};

export const getVisibleProfileItems = (items, isViewingOwn) => {
  if (!items?.length) return [];
  if (isViewingOwn) return items;
  return items.filter((item) => !isProfileItemDisabled(item));
};

/** Public resume / PDF views — hide disabled entries unless synced to resume. */
export const getVisibleResumeItems = (items) => {
  if (!items?.length) return [];
  return items.filter(
    (item) => !isProfileItemDisabled(item) || item?.isSyncedWithResume === true,
  );
};

const pickNonEmptyList = (primary = [], fallback = []) =>
  primary?.length ? primary : fallback || [];

const normalizeResumeExperiences = (experiences = []) =>
  experiences.map((exp) => ({
    ...exp,
    rsmExperienceAchievements:
      exp.rsmExperienceAchievements || exp.viewExperienceAchivements || [],
    rsmExperienceRoles:
      exp.rsmExperienceRoles || exp.viewExperienceRoles || [],
  }));

/** Combine authenticated resume API data with public profile fallback. */
export const mergeResumeSources = (resumeData, profileMapped) => {
  if (!resumeData && !profileMapped) return null;
  if (!resumeData) return profileMapped;
  if (!profileMapped) {
    return {
      ...resumeData,
      resumeExperiences: normalizeResumeExperiences(resumeData.resumeExperiences),
    };
  }

  const resumeInfo = resumeData.resumeInfo || {};
  const profileInfo = profileMapped.resumeInfo || {};

  return {
    resumeInfo: {
      ...profileInfo,
      ...resumeInfo,
      fullName: resumeInfo.fullName || profileInfo.fullName,
      userImage: resumeInfo.userImage || profileInfo.userImage,
      summary: resumeInfo.summary || profileInfo.summary,
      title: resumeInfo.title || profileInfo.title,
      email: resumeInfo.email || profileInfo.email,
      dob: resumeInfo.dob || profileInfo.dob,
      nationalityCountry:
        resumeInfo.nationalityCountry || profileInfo.nationalityCountry,
      currentCity: resumeInfo.currentCity || profileInfo.currentCity,
      phoneNumber: resumeInfo.phoneNumber || profileInfo.phoneNumber,
      phoneCode: resumeInfo.phoneCode || profileInfo.phoneCode,
      webSiteLink: resumeInfo.webSiteLink || profileInfo.webSiteLink,
      userUid: resumeInfo.userUid || profileInfo.userUid,
    },
    resumeEducations: pickNonEmptyList(
      resumeData.resumeEducations,
      profileMapped.resumeEducations,
    ),
    resumeExperiences: normalizeResumeExperiences(
      pickNonEmptyList(
        resumeData.resumeExperiences,
        profileMapped.resumeExperiences,
      ),
    ),
    resumeCertifications: pickNonEmptyList(
      resumeData.resumeCertifications,
      profileMapped.resumeCertifications,
    ),
    resumeAwards: pickNonEmptyList(
      resumeData.resumeAwards,
      profileMapped.resumeAwards,
    ),
    resumeSkills: pickNonEmptyList(
      resumeData.resumeSkills,
      profileMapped.resumeSkills,
    ),
    resumeAttachments: pickNonEmptyList(
      resumeData.resumeAttachments,
      profileMapped.resumeAttachments,
    ),
  };
};

/** Map public profile API response into resume view shape (QR / guest access). */
export const mapPublicProfileToResume = (profileData) => {
  if (!profileData?.profileInfo) return null;

  const info = profileData.profileInfo;
  const fullName = [info.firstName, info.lastName].filter(Boolean).join(" ").trim();

  return {
    resumeInfo: {
      ...info,
      fullName: fullName || info.fullName,
      userImage: info.profilPic || info.userImage,
      summary: info.about || info.summary,
      webSiteLink: info.website || info.webSiteLink,
      nationalityCountry: info.countryName || info.nationalityCountry,
      currentCity: info.cityName || info.currentCity,
    },
    resumeEducations: profileData.viewEducationInfo || [],
    resumeExperiences: normalizeResumeExperiences(
      profileData.viewExperienceInfo || [],
    ),
    resumeCertifications: profileData.viewCertificationInfo || [],
    resumeAwards: profileData.viewAwardsInfo || [],
    resumeSkills: profileData.viewSkillInfo || [],
    resumeAttachments: [],
  };
};
